import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { easeExpo } from "@/lib/motion";

const MANIFESTO = [
  {
    n: "01",
    title: "Global sourcing",
    body: "Six regional desks and 200+ vetted upstream suppliers. If it's manufactured, we can source it — even under allocation.",
  },
  {
    n: "02",
    title: "Genuine products",
    body: "Every unit passes lot-code inspection, X-ray verification and functional test in-house. Counterfeits don't get shipped.",
  },
  {
    n: "03",
    title: "Fast procurement",
    body: "Quotes in under 24 hours, PO to dispatch in 72 for stocked items. Rush lanes when your line is down.",
  },
  {
    n: "04",
    title: "Bulk availability",
    body: "Reel, tray or bulk — we hold inventory across three bonded warehouses so scale doesn't compromise lead time.",
  },
  {
    n: "05",
    title: "Worldwide shipping",
    body: "DDP, EXW, FCA — priced in USD, EUR, AED, SGD. Customs brokerage handled by our in-house desk.",
  },
  {
    n: "06",
    title: "Competitive pricing",
    body: "Direct relationships with authorized distributors and Tier-1 manufacturers. No spot-market surprises.",
  },
];

export default function WhyOmniCoreTek() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });
  const beamY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="why"
      className="relative py-28 md:py-40 border-t border-white/5"
      data-testid="section-why"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-14">
          <div className="lg:col-span-1 text-[11px] font-mono tracking-[0.22em] uppercase text-electric-500">
            03
          </div>
          <div className="lg:col-span-11">
            <div className="text-[11px] font-mono tracking-[0.22em] uppercase text-slate-400 mb-4">
              / Why OmniCoreTek
            </div>
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, ease: easeExpo }}
              className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.98] text-white max-w-[1000px]"
            >
              A manifesto for
              <br />
              <span className="text-slate-400">buyers who can&apos;t afford</span>
              <br />
              a wrong ship&#8209;out.
            </motion.h2>
          </div>
        </div>

        <div ref={containerRef} className="relative">
          {/* Tracing beam */}
          <div className="hidden md:block absolute left-[54px] top-0 bottom-0 w-px bg-black/8">
            <motion.div
              style={{ top: beamY }}
              className="absolute -translate-y-1/2 w-px h-40 bg-gradient-to-b from-transparent via-electric-500 to-transparent"
            />
          </div>

          <ul className="relative">
            {MANIFESTO.map((m, i) => (
              <motion.li
                key={m.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-120px" }}
                transition={{ duration: 0.9, ease: easeExpo }}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 md:py-12 border-b border-white/5 group"
                data-testid={`why-item-${m.n}`}
              >
                <div className="md:col-span-2 flex items-center gap-4">
                  <div className="w-3 h-3 rounded-full border border-electric-500/70 relative">
                    <span className="absolute inset-1 rounded-full bg-electric-500" />
                  </div>
                  <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-slate-400">
                    {m.n}
                  </div>
                </div>
                <div className="md:col-span-6">
                  <h3 className="font-display text-3xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1] text-white group-hover:text-electric-500 transition-colors duration-500">
                    {m.title}
                  </h3>
                </div>
                <div className="md:col-span-4">
                  <p className="text-slate-300 text-[15px] md:text-base leading-relaxed max-w-[400px]">
                    {m.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
